import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { type ReactNode } from 'react';
const SCAN_COUNT_KEY = 'smart_feedback_scan_count';
const REWRITE_COUNT_KEY = 'smart_feedback_rewrite_count';
const MILESTONES = [1, 5, 10, 20, 50, 100] as const;
const FIRST_SCAN_PENDING_KEY = 'smart_feedback_first_scan_pending';
type FeedbackMilestone = (typeof MILESTONES)[number] | null;
export type FeedbackMilestoneType = 'scan' | 'rewrite';
interface SmartFeedbackContextValue {
  isOpen: boolean;
  milestone: FeedbackMilestone;
  milestoneType: FeedbackMilestoneType | null;
  firstScanPromptPending: boolean;
  releaseFirstScanPrompt: () => void;
  openLandingFeedback: () => void;
  closeFeedback: () => void;
}
const SmartFeedbackContext = createContext<SmartFeedbackContextValue>({
  isOpen: false,
  milestone: null,
  milestoneType: null,
  firstScanPromptPending: false,
  releaseFirstScanPrompt: () => {},
  openLandingFeedback: () => {},
  closeFeedback: () => {}
});
interface SmartFeedbackProviderProps {
  children: ReactNode;
}
/**
 * Asks for a review at meaningful milestones only — the 1st, 5th, 10th, 20th,
 * 50th and 100th completed scan, and the same milestones for the message
 * rewriter. Scans and rewrites are counted separately so a heavy rewriter user
 * still gets asked, and each milestone is only ever shown once per browser.
 */
export function SmartFeedbackProvider({
  children
}: SmartFeedbackProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [milestone, setMilestone] = useState<FeedbackMilestone>(null);
  const [milestoneType, setMilestoneType] = useState<FeedbackMilestoneType | null>(null);
  const promptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPendingPrompt = useCallback(() => {
    if (promptTimerRef.current) {
      clearTimeout(promptTimerRef.current);
      promptTimerRef.current = null;
    }
  }, []);
  const shownKeyFor = (type: FeedbackMilestoneType, reached: number) => type === 'rewrite' ? `smart_feedback_rewrite_shown_${reached}` : `smart_feedback_shown_${reached}`;
  // The first-scan review waits for real engagement with the results; the
  // results page releases it via releaseFirstScanPrompt().
  const [firstScanPromptPending, setFirstScanPromptPending] = useState(() => {
    try {
      return localStorage.getItem(FIRST_SCAN_PENDING_KEY) === 'true' && localStorage.getItem('smart_feedback_shown_1') !== 'true';
    } catch {
      return false;
    }
  });
  const releaseFirstScanPrompt = useCallback(() => {
    localStorage.removeItem(FIRST_SCAN_PENDING_KEY);
    setFirstScanPromptPending(false);
    if (localStorage.getItem('smart_feedback_shown_1') === 'true') return;
    setMilestone(1);
    setMilestoneType('scan');
    setIsOpen(true);
  }, []);
  const scheduleMilestonePrompt = useCallback((count: number, type: FeedbackMilestoneType) => {
    const reachedMilestone = MILESTONES.find(item => item === count);
    if (!reachedMilestone) return;
    if (localStorage.getItem(shownKeyFor(type, reachedMilestone)) === 'true') return;
    if (type === 'scan' && reachedMilestone === 1) {
      localStorage.setItem(FIRST_SCAN_PENDING_KEY, 'true');
      setFirstScanPromptPending(true);
      return;
    }
    clearPendingPrompt();
    promptTimerRef.current = setTimeout(() => {
      setMilestone(reachedMilestone);
      setMilestoneType(type);
      setIsOpen(true);
      promptTimerRef.current = null;
    }, 1800);
  }, [clearPendingPrompt]);
  useEffect(() => {
    const bumpCount = (storageKey: string, type: FeedbackMilestoneType) => {
      const currentCount = Number.parseInt(localStorage.getItem(storageKey) || '0', 10);
      const nextCount = currentCount + 1;
      localStorage.setItem(storageKey, String(nextCount));
      scheduleMilestonePrompt(nextCount, type);
    };
    const handleScanCompleted = () => bumpCount(SCAN_COUNT_KEY, 'scan');
    const handleRewriteCompleted = () => bumpCount(REWRITE_COUNT_KEY, 'rewrite');
    window.addEventListener('scanCompleted', handleScanCompleted);
    window.addEventListener('rewriteCompleted', handleRewriteCompleted);
    return () => {
      window.removeEventListener('scanCompleted', handleScanCompleted);
      window.removeEventListener('rewriteCompleted', handleRewriteCompleted);
      clearPendingPrompt();
    };
  }, [clearPendingPrompt, scheduleMilestonePrompt]);
  const openLandingFeedback = useCallback(() => {
    clearPendingPrompt();
    setMilestone(null);
    setMilestoneType(null);
    setIsOpen(true);
  }, [clearPendingPrompt]);
  const closeFeedback = useCallback(() => {
    if (milestone && milestoneType) {
      localStorage.setItem(shownKeyFor(milestoneType, milestone), 'true');
    }
    // First scan review is done — let the results page follow up with sharing.
    if (milestone === 1 && milestoneType === 'scan') {
      localStorage.setItem('first_scan_share_prompt_pending', 'true');
      window.dispatchEvent(new CustomEvent('firstScanFeedbackClosed'));
    }
    clearPendingPrompt();
    setIsOpen(false);
    setMilestone(null);
    setMilestoneType(null);
  }, [clearPendingPrompt, milestone, milestoneType]);
  return <SmartFeedbackContext.Provider value={{
    isOpen,
    milestone,
    milestoneType,
    firstScanPromptPending,
    releaseFirstScanPrompt,
    openLandingFeedback,
    closeFeedback
  }} data-id="element-3251" data-mp="153jwuvzxb8ye">
      {children}
    </SmartFeedbackContext.Provider>;
}
export function useSmartFeedback() {
  return useContext(SmartFeedbackContext);
}